import { useEffect } from 'react';

// Single global listener so handlers don't fight each other across components.
// Use the simple "match → run → return" form. We deliberately ignore events
// that originate inside a text input — typing 'c' in the search box should
// type 'c', not claim a ticket.

export type ShortcutHandler = (e: KeyboardEvent) => void;

export interface Shortcut {
  /** Single character or named key (e.g. '/', 'Enter', 'Escape', 'j') */
  key: string;
  /** True if Ctrl OR Cmd is required */
  ctrl?: boolean;
  shift?: boolean;
  handler: ShortcutHandler;
  /** When true, the handler runs even if focus is in an input/textarea. Default false. */
  allowInInput?: boolean;
}

function inEditableField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(shortcuts: Shortcut[]): void {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      for (const s of shortcuts) {
        if (e.key !== s.key) continue;
        if (!!s.ctrl !== (e.ctrlKey || e.metaKey)) continue;
        if (!!s.shift !== e.shiftKey) continue;
        if (!s.allowInInput && inEditableField(e.target)) continue;
        s.handler(e);
        return;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcuts]);
}
